import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "domus-dynamic-import-reload";
const RELOAD_PARAM = "__df_asset_retry";

export const isDynamicImportError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  return /Importing a module script failed|Failed to fetch dynamically imported module|Loading chunk|ChunkLoadError/i.test(message);
};

const currentUrl = () => {
  try {
    return new URL(window.location.href);
  } catch {
    return null;
  }
};

const hasReloadMarker = () => {
  try {
    if (sessionStorage.getItem(RELOAD_KEY) === "true") return true;
  } catch {
    // sessionStorage may be unavailable in restricted browser modes
  }

  const url = currentUrl();
  return url?.searchParams.has(RELOAD_PARAM) ?? false;
};

export const clearDynamicImportReloadMarker = () => {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // sessionStorage may be unavailable in restricted browser modes
  }

  const url = currentUrl();
  if (url?.searchParams.has(RELOAD_PARAM)) {
    url.searchParams.delete(RELOAD_PARAM);
    window.history.replaceState(window.history.state, "", url.toString());
  }
};

export const reloadForFreshDynamicImports = () => {
  try {
    sessionStorage.setItem(RELOAD_KEY, "true");
  } catch {
    // The URL marker below still prevents repeated reloads if storage is blocked
  }

  const url = currentUrl();
  if (url) {
    url.searchParams.set(RELOAD_PARAM, Date.now().toString());
    window.location.replace(url.toString());
    return;
  }

  window.location.reload();
};

const waitForReload = <T extends ComponentType<any>>() => new Promise<{ default: T }>(() => {});

/**
 * React.lazy with resilience against stale chunk URLs after a new deploy.
 * Retries once, then forces one cache-busting reload to fetch the fresh app shell.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      clearDynamicImportReloadMarker();
      return mod;
    } catch (error) {
      if (!isDynamicImportError(error)) throw error;

      try {
        const mod = await factory();
        clearDynamicImportReloadMarker();
        return mod;
      } catch (retryError) {
        if (!isDynamicImportError(retryError)) throw retryError;

        if (!hasReloadMarker()) {
          reloadForFreshDynamicImports();
          return waitForReload<T>();
        }

        throw retryError;
      }
    }
  });
}
